// src/components/Navbar.tsx
"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import { useUser } from "@/providers/userContext";
import { useDispatch } from "react-redux";
import { getLogo, getStoreTitle } from "@/services/storeService";
import { AxiosPost_Nocatch } from "@/utilities/axioscall";
import { AK } from "@/constants";
import SvgIcons from "@/components/themes/SvgIcons";
import useFCM from "@/app/fireutils/hooks/useFCM";
import SearchBar from "../themes/SearchBar";
import { Search } from "lucide-react";

const Navbar = ({ categoryListData }: any) => {
  const pathname = usePathname();
  const router = useRouter();
  const { login, isLoggedIn, logout, user, cartinfoData } = useUser();
  const { fcmToken, messages, requestPermission, showPrompt } = useFCM();
  const [isMobile, setIsMobile] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [wishlist, setWishlist] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [userData, setUserData] = useState<any>(user);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);
  const [activeMenuItem, setActiveMenuItem] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Memoize main categories with display_in_menu === 1
  const mainCategories = useMemo(
    () =>
      categoryListData
        ?.filter(
          (category: { parent_id: number; display_in_menu: number }) =>
            category.parent_id === 0 && category.display_in_menu === 1
        )
        .sort((a: { id: number }, b: { id: number }) => a.id - b.id),
    [categoryListData]
  );

  // Memoize subcategory function
  const getSubcategoriesByParentId = useMemo(
    () => (parentId: number) =>
      categoryListData?.filter(
        (category: any) => category.parent_id === parentId
      ),
    [categoryListData]
  );


  // Handle body scroll for mobile menu
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  // Handle mobile detection and mounting
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    const handleResize = () => setTimeout(checkMobile, 150);
    window.addEventListener("resize", handleResize);
    setHasMounted(true);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update user data and cart count
  useEffect(() => {
    setUserData(user ?? null);
  }, [user]);

  useEffect(() => {
    setCartCount(cartinfoData?.cartcount ?? 0);
  }, [cartinfoData]);

  // Navbar visibility logic
  const hiddenPaths = ["/signin"];
  const shouldHideNavbar =
    hiddenPaths.includes(pathname) ||
    pathname.startsWith("/p/") ||
    pathname.startsWith("/cart") ||
    pathname.startsWith("/checkout");

  const handleItemClick = (menuItem: string) => {
    setActiveMenuItem(menuItem);
  };

  const handleNavigate = (path: string) => {
    router.push(path);
    setMenuOpen(false);
  };

  const toggleSubcategory = (categoryId: number) => {
    setExpandedCategoryIds((prevIds) =>
      prevIds.includes(categoryId)
        ? prevIds.filter((id) => id !== categoryId)
        : [...prevIds, categoryId]
    );
  };

  const RenderSubcategories = ({ parentId }: { parentId: number }) => {
    const subcategories = getSubcategoriesByParentId(parentId);
    if (!subcategories?.length) return null;

    return (
      <ul className="ml-4 mt-2 border-l pl-4">
        {subcategories.map((subCategory: any) => (
          <li key={subCategory.id} className="py-1">
            <div className="flex justify-between items-center">
              <Link
                href={`/products/${subCategory.seo_url}`}
                className="text-sm hover:text-green-600"
              >
                {subCategory.name}
              </Link>
              {getSubcategoriesByParentId(subCategory.id).length > 0 && (
                <button
                  onClick={() => toggleSubcategory(subCategory.id)}
                  className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-expanded={expandedCategoryIds.includes(subCategory.id)}
                  aria-label={`Toggle ${subCategory.name} subcategories`}
                >
                  <FontAwesomeIcon
                    icon={
                      expandedCategoryIds.includes(subCategory.id)
                        ? faChevronDown
                        : faChevronRight
                    }
                  />
                </button>
              )}
            </div>

            {expandedCategoryIds.includes(subCategory.id) && (
              <RenderSubcategories parentId={subCategory.id} />
            )}
          </li>
        ))}
      </ul>
    );
  };

  const searchParams = useSearchParams();

  const handleLogout = async () => {
    try {
      console.log("Initiating logout...");
      await AxiosPost_Nocatch(AK.LOGOUTAPI, {}, true);

      logout();
      console.log("User data cleared");

      // Build current full path
      const currentPath = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");

      // App Router: push takes only a string
      router.push(`/signin?from=${encodeURIComponent(currentPath)}`);

    } catch (error: any) {
      console.error("Logout failed:", error);
      logout();

      const currentPath = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");
      router.push(`/signin?from=${encodeURIComponent(currentPath)}`);
    }
  };
  const currentPath = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');

  const href = `/signin?from=${encodeURIComponent(currentPath)}`;

  const isCartPage = pathname === "/cart";
  const isWishlist = pathname === "/my-wishlist";
  const isSignin = hiddenPaths.includes(pathname);

  if (!hasMounted || (isMobile && shouldHideNavbar)) return null;

  return (
    <>
      <div className="bg-black text-center text-white text-[8px] py-1">*SHIPPING WORLDWIDE*</div>
      <nav
        className="flex justify-between items-center"
        style={{ position: "sticky", top: 0, zIndex: 100 }}
      >
        {/* Mobile Header */}
        <div className="sm:hidden flex justify-between items-center py-3 px-2 w-full bg-white">
          <button
            className="p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#000"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
          </button>

          <Link href="/">
            <LazyLoadImage
              className="mx-auto"
              width="80"
              src={`${getLogo()}`}
              onError={(e) => {
                e.currentTarget.src =
                  "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
              }}
              alt={`${getStoreTitle()}`}
            />
          </Link>

          <div className="flex items-center gap-2">

            <Search />

            <Link href="/cart" className="relative pr-2">
              <SvgIcons.NavCartIcon isCartPage={isCartPage} />
              {cartCount > 0 && <span className="text-white text-sm bg-red-600 flex items-center justify-center rounded-full absolute -top-1 right-0 h-4 w-4">{cartCount}</span>}
            </Link>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden bg-white sm:flex w-full px-3 md:px-5 lg:px-10 py-2 justify-between items-center">
          <div className="nav_logo">
            <Link href="/">
              <LazyLoadImage
                className="mx-auto"
                width="100"
                src={`${getLogo()}`}
                onError={(e) => {
                  e.currentTarget.src =
                    "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
                }}
                alt={`${getStoreTitle()}`}
              />
            </Link>
          </div>

          <div className="flex-grow mx-4">
            <SearchBar isMobile={false} onNavigate={handleNavigate} />
          </div>

          <div className="flex items-center gap-4">
            <Link
              href={isWishlist ? "/products/traditional-rices" : "/my-wishlist"}
              onClick={() => setWishlist(!wishlist)}
              className="relative flex flex-col items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
              >
                <path
                  d="M19.5 14.3423C20.99 12.8823 22.5 11.1323 22.5 8.84229C22.5 7.38359 21.9205 5.98465 20.8891 4.9532C19.8576 3.92175 18.4587 3.34229 17 3.34229C15.24 3.34229 14 3.84229 12.5 5.34229C11 3.84229 9.76 3.34229 8 3.34229C6.54131 3.34229 5.14236 3.92175 4.11091 4.9532C3.07946 5.98465 2.5 7.38359 2.5 8.84229C2.5 11.1423 4 12.8923 5.5 14.3423L12.5 21.3423L19.5 14.3423Z"
                  stroke={isWishlist ? "#629A28" : "#000"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

            </Link>

            <Link href="/cart" className="relative flex flex-col items-center">
              <SvgIcons.NavCartIcon isCartPage={isCartPage} />
              {cartCount > 0 && <span className="text-white text-sm bg-red-600 flex items-center justify-center rounded-full absolute -top-1 -right-2 h-4 w-4">{cartCount}</span>}
            </Link>

            {userData ? (
              <div className="relative inline-block">
                <div className="flex cursor-pointer flex-col items-center" onMouseEnter={() => setIsOpen(true)}
                  onMouseLeave={() => setIsOpen(false)}>
                  <SvgIcons.NavUserIcon />
                  <span className="text-xs font-normal uppercase">
                    {userData.name || "Guest"}
                  </span>
                </div>
                {isOpen && (
                  <div
                    className="absolute right-0 top-2 mt-2 w-32 bg-white rounded-md shadow-lg z-50
                     border border-gray-200 overflow-hidden"
                    onMouseEnter={() => setIsOpen(true)}   // keep open while cursor is inside
                    onMouseLeave={() => setIsOpen(false)}  // close when cursor leaves
                  >
                    <Link
                      href="/my-account"
                      className={`block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100
                        ${activeMenuItem === 'my-account' ? 'bg-gray-100 font-medium' : ''}`}
                      onClick={() => {
                        handleItemClick('my-account');
                        setIsOpen(false); // optional: close after click
                      }}
                    >
                      My Account
                    </Link>

                    <hr className="border-gray-200" />

                    <button
                      type="button"
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link href={href} className="flex  gap-2items-center">
                <SvgIcons.NavSigninIcon isSignin={isSignin} />
                <span
                  className="text-md font-normal"
                  style={{ color: isSignin ? "#629A28" : "#000" }}
                >
                  Login
                </span>
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Side Menu */}
        {menuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[9999]"
            onClick={() => setMenuOpen(false)}
          >
            <div
              className=" bg-black text-white w-4/5 h-full px-4 overflow-auto transition-transform duration-300"
              style={
                menuOpen
                  ? { transform: "translateX(0)" }
                  : { transform: "translateX(-100%)" }
              }
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0  bg-black">
                <div className="relative">
                  <Link href="/" className="block py-2">
                    <LazyLoadImage
                      className="mx-auto mb-3"
                      width="80"
                      src={`${getLogo()}`}
                      onError={(e) => {
                        e.currentTarget.src =
                          "https://vilva-backup.blr1.digitaloceanspaces.com/vilvabusiness/common%20files/empty-logowebp.webp";
                      }}
                      alt={`${getStoreTitle()}`}
                    />
                  </Link>
                  <button
                    className="absolute top-2 right-2"
                    onClick={() => setMenuOpen(false)}
                    aria-label="Close mobile menu"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#393B40"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 18L18 6M6 6L18 18" />
                    </svg>
                  </button>
                </div>
                <div className="flex text-white justify-between items-center py-2">
                  <div className="flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                    >
                      <path
                        d="M18 20.3423C18 18.751 17.3679 17.2249 16.2426 16.0996C15.1174 14.9744 13.5913 14.3423 12 14.3423C10.4087 14.3423 8.88258 14.9744 7.75736 16.0996C6.63214 17.2249 6 20.3423"
                        stroke={userData ? "#629A28" : "#fff"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 14.3423C14.2091 14.3423 16 12.5514 16 10.3423C16 8.13315 14.2091 6.34229 12 6.34229C9.79086 6.34229 8 8.13315 8 10.3423C8 12.5514 9.79086 14.3423 12 14.3423Z"
                        stroke={userData ? "#629A28" : "#fff"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 22.3423C17.5228 22.3423 22 17.8651 22 12.3423C22 6.81944 17.5228 2.34229 12 2.34229C6.47715 2.34229 2 6.81944 2 12.3423C2 17.8651 6.47715 22.3423 12 22.3423Z"
                        stroke={userData ? "#629A28" : "#fff"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <Link
                      href={href}
                      className="text-sm uppercase"
                      style={{ color: userData ? "#629A28" : "#fff" }}
                    >
                      {userData ? `Hi ${userData.name}!` : "Sign In"}
                    </Link>
                  </div>
                  <Link href="/my-wishlist">
                    <svg
                      width="23"
                      height="20"
                      viewBox="0 0 23 20"
                      fill={isWishlist ? "#629A28" : "none"}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.4998 19.2061L2.70115 9.92527C1.92859 9.14433 1.41864 8.1374 1.24355 7.04712C1.06847 5.95684 1.23713 4.8385 1.72563 3.85053C2.09464 3.10462 2.63366 2.45803 3.29828 1.96406C3.9629 1.47008 4.73408 1.14284 5.5483 1.00931C6.36252 0.875782 7.19647 0.939779 7.98144 1.19603C8.7664 1.45228 9.47991 1.89345 10.0632 2.48319L11.4998 3.93577L12.9364 2.48319C13.5197 1.89345 14.2332 1.45228 15.0182 1.19603C15.8031 0.939779 16.6371 0.875782 17.4513 1.00931C18.2655 1.14284 19.0367 1.47008 19.7013 1.96406C20.3659 2.45803 20.905 3.10462 21.274 3.85053C21.7625 4.8385 21.9311 5.95684 21.756 7.04712C21.581 8.1374 21.071 9.14433 20.2984 9.92527L11.4998 19.2061Z"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </div>
              </div>

              <ul className="text-white">
                <li className="py-2">
                  <Link href="/" className="text-base hover:text-green-600">
                    Home
                  </Link>
                </li>
                {mainCategories?.length > 0 ? (
                  mainCategories.map((category: any) => (
                    <li className="py-2" key={category.id}>
                      <div className="flex justify-between items-center">
                        <Link
                          href={`/products/${category.seo_url}`}
                          className="text-base hover:text-green-600"
                        >
                          {category.name}
                        </Link>
                        {getSubcategoriesByParentId(category.id).length > 0 && (
                          <button
                            onClick={() => toggleSubcategory(category.id)}
                            className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                            aria-expanded={expandedCategoryIds.includes(
                              category.id
                            )}
                            aria-label={`Toggle ${category.name} subcategories`}
                          >
                            <FontAwesomeIcon
                              icon={
                                expandedCategoryIds.includes(category.id)
                                  ? faChevronDown
                                  : faChevronRight
                              }
                            />
                          </button>
                        )}
                      </div>
                      {expandedCategoryIds.includes(category.id) && (
                        <RenderSubcategories parentId={category.id} />
                      )}
                    </li>
                  ))
                ) : (
                  <li className="py-2">Loading categories...</li>
                )}
                {userData && (
                  <ul className="bg-white">
                    <li className="py-2">
                      <h5 className="font-semibold">MY ACCOUNT</h5>
                    </li>
                    <li className="py-2">
                      <Link href="/my-account" className="hover:text-green-600">
                        My Account
                      </Link>
                    </li>
                  </ul>
                )}
                {userData && (
                  <li className="sticky bottom-0 bg-white py-2 flex justify-between items-center">
                    <div
                      onClick={() => handleLogout()}
                      className="hover:text-green-600"
                    >
                      Log Out
                    </div>
                    <div onClick={() => handleLogout()}>
                      <FontAwesomeIcon icon={faRightFromBracket} />
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </nav>


    </>
  );
};

export default Navbar;